/**
 * Экран утверждения стартового предложения набора тем (#845, PR 4; спека §2 Р1).
 *
 * Список тем, которые модель собрала из `topic` разговоров за 90 дней: у
 * каждой — число разговоров и доля (их считает сервер по сохранённым номерам
 * фраз, не модель), охват «предложено по N % разговоров» и доля «без темы».
 * Администратор правит названия и определения, снимает лишние и утверждает —
 * отмеченные темы входят в набор как действующие. Те же правила поля, что у
 * ручного ввода (`themeFieldProblem`), и то же предупреждение «тема ≠
 * категория» (§2.1) — предупреждение, не запрет.
 *
 * Утверждение несёт `requestedAt` показанного предложения: пересозданное тем
 * временем сервер не утвердит (409), а повтор не заведёт тему дважды. Отказ
 * утверждения показывает родитель (`onApproveError`): после него состояние
 * перечитывается, и этот экран может исчезнуть или смениться новым списком —
 * сообщение не должно уйти вместе с ним.
 */
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { findThemeOverlaps } from '@aica/core/voc/theme-overlap';
import {
  ORG_THEME_DEFINITION_MAX,
  ORG_THEME_NAME_MAX,
  type ApproveThemeBootstrapResponse,
  type ThemeBootstrapProposal,
} from '@aica/api-types/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { overlapNames } from './overlap-names';
import { themeFieldProblem } from './theme-field-problem';
import { bootstrapErrorMessage } from './bootstrap-error-message';
import { useApproveThemeBootstrap } from './useThemeBootstrap';

interface Draft {
  include: boolean;
  name: string;
  definition: string;
}

interface VoiceThemeBootstrapProposalProps {
  proposal: ThemeBootstrapProposal;
  requestedAt: string;
  /** The «suggest again» control — owned by the parent, shown under the list. */
  regenerate: ReactNode;
  /** The editor is read-only: a «suggest again» is in flight; the drafts are kept. */
  disabled: boolean;
  /**
   * The approval failed (a message) or a new one starts (`null`). The parent
   * shows it: the re-read of the state that follows may unmount this screen or
   * replace it with a renewed proposal (`bootstrap_changed`), and a message
   * held here would go with it.
   */
  onApproveError: (message: string | null) => void;
  /**
   * The approval went through. The parent keeps the outcome: this screen
   * disappears when the state is re-read (the proposal is approved), and the
   * message must not go with it.
   */
  onApproved: (response: ApproveThemeBootstrapResponse) => void;
}

const percent = (part: number, whole: number): number =>
  whole > 0 ? Math.round((part / whole) * 100) : 0;

export function VoiceThemeBootstrapProposal({
  proposal,
  requestedAt,
  regenerate,
  disabled,
  onApproveError,
  onApproved,
}: VoiceThemeBootstrapProposalProps) {
  const { t } = useTranslation(['dashboard', 'records']);
  const id = useId();
  const approve = useApproveThemeBootstrap(onApproved);
  const [drafts, setDrafts] = useState<Draft[]>(() =>
    proposal.themes.map((theme) => ({
      include: true,
      name: theme.name,
      definition: theme.definition,
    }))
  );

  const update = (index: number, patch: Partial<Draft>) =>
    setDrafts((current) =>
      current.map((draft, i) => (i === index ? { ...draft, ...patch } : draft))
    );

  const included = drafts.filter((draft) => draft.include);
  const invalid = included.some(
    (draft) =>
      themeFieldProblem('name', draft.name) !== null ||
      themeFieldProblem('definition', draft.definition) !== null
  );

  const readOnly = disabled || approve.isPending;

  const submit = async () => {
    onApproveError(null);
    try {
      await approve.mutateAsync({
        requestedAt,
        themes: included.map((draft) => ({
          name: draft.name.trim(),
          definition: draft.definition.trim(),
        })),
      });
    } catch (failure) {
      onApproveError(await bootstrapErrorMessage(failure, t, 'approve'));
    }
  };

  const total = proposal.totalCalls ?? 0;

  return (
    <div className="space-y-4">
      {proposal.sharesAvailable && total > 0 ? (
        <div className="space-y-1 text-sm text-fg-muted">
          <p>
            {t('voicePage.themes.bootstrap.coverage', {
              percent: percent(proposal.coveredCalls ?? 0, total),
              total,
            })}
          </p>
          <p>
            {t('voicePage.themes.bootstrap.noTheme', {
              percent: percent(total - (proposal.themedCalls ?? 0), total),
            })}
          </p>
          {proposal.sampled && <p>{t('voicePage.themes.bootstrap.sampled')}</p>}
          {proposal.sharesApproximate && (
            <p>{t('voicePage.themes.bootstrap.sharesApproximate')}</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-fg-muted">
          {proposal.sharesStatus === 'unavailable'
            ? t('voicePage.themes.bootstrap.sharesUnavailable')
            : proposal.sharesStatus === 'empty'
              ? t('voicePage.themes.bootstrap.sharesEmpty')
              : t('voicePage.themes.bootstrap.noShares')}
        </p>
      )}

      <ul className="space-y-3">
        {proposal.themes.map((theme, index) => {
          const draft = drafts[index]!;
          const nameProblem = themeFieldProblem('name', draft.name);
          const definitionProblem = themeFieldProblem('definition', draft.definition);
          const overlaps = draft.name === theme.name ? [] : findThemeOverlaps(draft.name);
          const rowId = `${id}-${index}`;
          return (
            <li
              key={rowId}
              className="space-y-2 rounded-card border border-border bg-bg-elevated px-3 py-3"
            >
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${rowId}-include`}
                  checked={draft.include}
                  disabled={readOnly}
                  onCheckedChange={(checked) =>
                    update(index, { include: checked === true })
                  }
                  aria-label={t('voicePage.themes.bootstrap.include', {
                    name: draft.name,
                  })}
                />
                <span className="text-sm text-fg-muted">
                  {theme.calls === null
                    ? t('voicePage.themes.bootstrap.shareUnknown')
                    : t('voicePage.themes.bootstrap.share', {
                        calls: theme.calls,
                        percent: percent(theme.calls, total),
                      })}
                </span>
                {theme.needsDefinition && (
                  <Badge variant="warning">
                    {t('voicePage.themes.bootstrap.toRefine')}
                  </Badge>
                )}
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor={`${rowId}-name`}
                  className="block text-xs font-medium text-fg-secondary"
                >
                  {t('voicePage.themes.form.name')}
                </label>
                <Input
                  id={`${rowId}-name`}
                  value={draft.name}
                  maxLength={ORG_THEME_NAME_MAX}
                  disabled={!draft.include || readOnly}
                  error={draft.include && nameProblem !== null}
                  aria-invalid={draft.include && nameProblem !== null}
                  onChange={(event) => update(index, { name: event.target.value })}
                />
                {draft.include && nameProblem !== null && (
                  <p className="text-xs text-danger">
                    {nameProblem === 'empty'
                      ? t('voicePage.themes.bootstrap.nameRequired')
                      : t(`voicePage.themes.form.problems.${nameProblem}`, {
                          max: ORG_THEME_NAME_MAX,
                        })}
                  </p>
                )}
                {draft.include && overlaps.length > 0 && (
                  <p
                    role="status"
                    className="flex items-start gap-2 rounded-card border border-warning/30 bg-warning-dim px-3 py-2 text-xs text-warning-fg"
                  >
                    <AlertTriangle
                      className="mt-0.5 h-4 w-4 shrink-0"
                      aria-hidden="true"
                    />
                    <span>
                      {t('voicePage.themes.form.overlapWarning', {
                        names: overlapNames(overlaps, t),
                      })}
                    </span>
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor={`${rowId}-definition`}
                  className="block text-xs font-medium text-fg-secondary"
                >
                  {t('voicePage.themes.form.definition')}
                </label>
                <Input
                  id={`${rowId}-definition`}
                  value={draft.definition}
                  maxLength={ORG_THEME_DEFINITION_MAX}
                  placeholder={t('voicePage.themes.form.definitionPlaceholder')}
                  disabled={!draft.include || readOnly}
                  error={draft.include && definitionProblem !== null}
                  aria-invalid={draft.include && definitionProblem !== null}
                  onChange={(event) => update(index, { definition: event.target.value })}
                />
                {draft.include && definitionProblem !== null && (
                  <p className="text-xs text-danger">
                    {t(`voicePage.themes.form.problems.${definitionProblem}`, {
                      max: ORG_THEME_DEFINITION_MAX,
                    })}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={included.length === 0 || invalid || readOnly}
          onClick={() => void submit()}
        >
          {t('voicePage.themes.bootstrap.approve', { selected: included.length })}
        </Button>
        {regenerate}
      </div>
    </div>
  );
}
